<template>
  <div>
    <div class="row">
      <div>
        <app-form-field
          id="deliveryDatesLabel"
          :title="retrieveModalText('delivery date')"
          :error-message="''"
        />
      </div>
      <div>
        <date-picker
          id="deliveryDate"
          :min-date="minDate"
          :date="deliveryDate"
          :title="''"
          :error-message="errorMessageDeliveryDate"
          :disabled="disabled"
          @change-date="changeDeliveryDate"
        />
        <div v-if="expectedDeliveryDatesEnabled" class="icon">
          <button @click="showInfoModal = !showInfoModal">
            <icon icon="info-plain" large />
          </button>
        </div>
      </div>
    </div>

    <modal
      v-if="showInfoModal"
      max-width="700px"
      :title="i18n.t('/expected delivery date/popup title')"
      @close="showInfoModal = false"
    >
      <expected-delivery-date-popup :is-purchase-order="isPurchaseOrder" />
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import AppFormField from '@/components/generic/presentational/atoms/formField/FormField.vue'
import { mapGetters, mapState } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import quoteOrderService from '@/services/quoteOrderService.js'
import purchaseOrderService from '@/services/purchaseOrderService.js'
import DeliveryDatesApi from '@/api/server/DeliveryDates.api'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import ExpectedDeliveryDatePopup from '@/components/generic/presentational/molecules/expectedDeliveryDatePopup/ExpectedDeliveryDatePopup.vue'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    DatePicker,
    Icon,
    Modal,
    ExpectedDeliveryDatePopup,
    AppFormField,
  },
  mixins: [AdditionalOrder],
  validations() {
    const isAfterMinDate = (value, options) => {
      const minDate = this.minDate

      return !helpers.req(value) || (value != 'INVALID' && value.isSameOrAfter(minDate, 'day'))
    }

    const isValidDate = (value) => {
      return !helpers.req(value) || moment.utc(value).isValid()
    }

    return {
      deliveryDate: {
        required,
        isAfterMinDate,
        isValidDate,
      },
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrder: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      deliveryDate: null,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showInfoModal: false,
    }
  },
  computed: {
    ...mapGetters({
      quoteId: 'quote/quoteId',
    }),
    ...mapState('user', ['dealerSettings']),
    ...mapState({
      expectedDeliveryDates(state, getters) {
        return getters[this.namespace + '/expectedDeliveryDates']
      },
    }),
    ...mapState({
      purchaseOrderHasOwner(state, getters) {
        return getters['purchaseOrder/hasOwner']
      },
      purchaseOrderConfigurationCode(state, getters) {
        return getters['purchaseOrder/configurationCode']
      },
    }),
    namespace() {
      return this.isPurchaseOrder ? 'purchaseOrder' : 'quote'
    },
    namespaceWithAdditionalOrder() {
      return this.isPurchaseOrder ? 'purchaseOrder' : this.quoteNamespace
    },
    expectedDeliveryDatesEnabled() {
      return this.dealerSettings.showDeliveryDates
    },
    errorMessageDeliveryDate() {
      if (this.v$.deliveryDate.$anyDirty) {
        if (!this.v$.deliveryDate.required.$response) return 'De leverdatum is verplicht'
        if (!this.v$.deliveryDate.isValidDate.$response)
          return 'Vul een geldige leverdatum in (dd-mm-jjjj)'
        if (!this.v$.deliveryDate.isAfterMinDate.$response) {
          return this.expectedDeliveryDateDealer
            ? 'Gewenste leverdatum klant ligt op of na de indicatieve leverdatum dealer'
            : 'Deze afleverdatum lijkt niet logisch graag controleren'
        }
      }
      return ''
    },
    defaultMinDate() {
      return moment.utc().startOf('day')
    },
    minDate() {
      return this.expectedDeliveryDateDealer
        ? moment.utc(this.expectedDeliveryDateDealer).startOf('day')
        : this.defaultMinDate
    },
    expectedDeliveryDateDealer() {
      return !this.allDatesSame && this.expectedDeliveryDates?.ExpectedDeliveryDateDealer
    },
    allDatesSame() {
      if (!this.expectedDeliveryDates) return true

      const expectedDeliveryDateValues = Object.values(this.expectedDeliveryDates)

      return expectedDeliveryDateValues.every((date, index) => {
        if (index > 0) {
          return moment.utc(date).isSame(moment.utc(expectedDeliveryDateValues[index - 1]), 'day')
        }
        return true
      })
    },
  },
  watch: {
    purchaseOrderHasOwner(newValue) {
      this.reloadExpectedDeliveryDates(!newValue)
    },
  },
  created() {
    this.deliveryDateUpdate()
    this.$nextTick(() => {
      this.v$.deliveryDate.$touch()
    })

    EventBus.$on('configuration-loaded', this.initExpectedDeliveryDates)
    EventBus.$on('quote-is-stock-changed', this.reloadExpectedDeliveryDates)
    EventBus.$on('change-delivery-date', this.changeDeliveryDate)
    EventBus.$on('quote/deliveryDate', this.deliveryDateUpdate)
    EventBus.$on('purchaseOrder/deliveryDate', this.deliveryDateUpdate)
  },
  unmounted() {
    EventBus.$off('configuration-loaded', this.initExpectedDeliveryDates)
    EventBus.$off('quote-is-stock-changed', this.reloadExpectedDeliveryDates)
    EventBus.$off('change-delivery-date', this.changeDeliveryDate)
    EventBus.$off('quote/deliveryDate', this.deliveryDateUpdate)
    EventBus.$off('purchaseOrder/deliveryDate', this.deliveryDateUpdate)
  },
  methods: {
    async initExpectedDeliveryDates() {
      if (this.expectedDeliveryDatesEnabled && !this.isPurchaseOrder) {
        DeliveryDatesApi.GetDeliveryDatesByQuoteId(this.quoteId).then((resp) => {
          if (resp) {
            this.$store.dispatch('quote/setExpectedDeliveryDates', resp).then(() => {
              EventBus.$emit('delivery-dates-loaded')
            })
          }
        })
      }
    },
    async reloadExpectedDeliveryDates(stockOrder) {
      if (this.expectedDeliveryDatesEnabled) {
        if (!this.isPurchaseOrder) {
          await this.initExpectedDeliveryDates()
        } else {
          const deliveryDates = await DeliveryDatesApi.GetDeliveryDatesByConfigurationCode(
            this.purchaseOrderConfigurationCode,
            stockOrder
          )
          this.$store.commit('purchaseOrder/setExpectedDeliveryDates', deliveryDates)
        }
      }
    },
    async changeDeliveryDate(value) {
      this.deliveryDate = value
      this.v$.deliveryDate.$touch()
      if (!this.v$.deliveryDate.$error) {
        // format to yyyy-MM-dd
        let formatedValue = value.toJSON().slice(0, 10)
        if (this.isPurchaseOrder) {
          await purchaseOrderService.updateDeliveryDate(formatedValue)
        } else {
          await quoteOrderService.updateDeliveryDate(formatedValue)
        }
      }
    },
    deliveryDateUpdate() {
      const stateDeliveryDate =
        this.$store.getters[`${this.namespaceWithAdditionalOrder}/deliveryDate`]
      this.deliveryDate = stateDeliveryDate ? moment.utc(stateDeliveryDate) : null
    },
    retrieveModalText(labelName) {
      return this.i18n.t(`/expected delivery date/${labelName}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.datepicker-container {
  min-height: 52px;
}

.bold-label {
  font-weight: $font-weight-bold;
}
</style>
